import React, {useState, useEffect} from "react";
import {
    Box, Typography, Paper, TextField, Button, CircularProgress,
    MenuItem, Select, FormControl, InputLabel, Alert,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import {showSnackbar} from "../../components/SnackBar/SnackBar";
import {accountingApi} from "../../services/api/accounting";
import {getLandlordId} from "../../services/api/client";

const MONTHS = [
    {value: 1, label: "January"}, {value: 2, label: "February"},
    {value: 3, label: "March"}, {value: 4, label: "April"},
    {value: 5, label: "May"}, {value: 6, label: "June"},
    {value: 7, label: "July"}, {value: 8, label: "August"},
    {value: 9, label: "September"}, {value: 10, label: "October"},
    {value: 11, label: "November"}, {value: 12, label: "December"},
];

export default function AccountingSettings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [taxRate, setTaxRate] = useState(7.5);
    const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState(1);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const landlordId = getLandlordId();
            const response = await accountingApi.getSettings(landlordId);
            if (response.ok && response.data) {
                setTaxRate(response.data.tax_rate ?? 7.5);
                setFiscalYearStartMonth(response.data.fiscal_year_start_month ?? 1);
            }
        } catch (err) {
            console.error("Failed to fetch settings:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const landlordId = getLandlordId();
            const response = await accountingApi.updateSettings({
                landlord_id: landlordId,
                tax_rate: parseFloat(taxRate),
                fiscal_year_start_month: fiscalYearStartMonth,
            });
            if (response.ok) {
                showSnackbar({title: "Success!", message: "Financial settings updated.", type: "success", position: {vertical: "bottom", horizontal: "right"}});
            } else {
                showSnackbar({title: "Error!", message: response.data?.Error || "Failed to update settings.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
            }
        } catch (err) {
            showSnackbar({title: "Error!", message: "Something went wrong.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{display: "flex", justifyContent: "center", py: 10}}><CircularProgress/></Box>;
    }

    return (
        <Box sx={{maxWidth: 600, mx: "auto", py: 3}}>
            <Typography variant="h5" fontWeight={600} sx={{mb: 3, color: "#2C5283"}}>
                Financial Settings
            </Typography>

            <Paper sx={{p: 3, borderRadius: 2}}>
                <Box sx={{mb: 3}}>
                    <TextField
                        label="Tax Rate (%)"
                        type="number"
                        value={taxRate}
                        onChange={(e) => setTaxRate(e.target.value)}
                        inputProps={{min: 0, max: 100, step: 0.01}}
                        fullWidth
                        helperText="KRA residential rental income tax rate is 7.5% as of 2026"
                    />
                </Box>

                <Box sx={{mb: 3}}>
                    <FormControl fullWidth>
                        <InputLabel>Fiscal Year Start Month</InputLabel>
                        <Select
                            value={fiscalYearStartMonth}
                            label="Fiscal Year Start Month"
                            onChange={(e) => setFiscalYearStartMonth(e.target.value)}
                        >
                            {MONTHS.map((m) => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Alert severity="info" sx={{mb: 3}}>
                    Changes to the tax rate will affect future report calculations. Existing journal entries are not modified.
                </Alert>

                <Button
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{background: "linear-gradient(45deg, #2C5283, #1E3A63)"}}
                >
                    {saving ? "Saving..." : "Save Settings"}
                </Button>
            </Paper>
        </Box>
    );
}
